import React, { FC, useRef } from 'react'
import styled from 'styled-components'
import { Text } from 'ui'
import { Step1Props } from './types'
import { BlackPointUpArrowIcon, ImageUploadIcon, MinusIcon } from '../icons'
import { SmallImageContainer, SmallImage } from '../SharedStyledComponents'

const FormField = styled.div`
  margin-bottom: 16px;
  width: 100%;
`

const Label = styled.div`
  margin-bottom: 8px;
  display: flex;
  justify-content: flex-start;
`

const Input = styled.input`
  background-color: #FFFFFF;
  border-radius: 8px;
  padding: 16px;
  color: #272727;
  font-size: 14px;
  text-align: right;
  min-height: 45px;
  width: 100%;
  border: none;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #B0B0B0;
  }
`

const TextArea = styled.textarea`
  background-color: #FFFFFF;
  border-radius: 8px;
  padding: 16px;
  color: #272727;
  font-size: 14px;
  text-align: right;
  min-height: 96px;
  width: 100%;
  border: none;
  outline: none;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &::placeholder {
    color: #B0B0B0;
  }
`

const CategoryDropdownContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  min-height: 45px;
  cursor: pointer;
  box-sizing: border-box;
`

const ImageUploadSection = styled.div`
  margin-top: 8px;
  margin-bottom: 24px;
  width: 100%;
`

const ImageUploadBox = styled.div<{ hasImages?: boolean }>`
  background-color: #FFFFFF;
  border-radius: 12px;
  border: 2px dashed #D1D1D1;
  padding: 12px;
  display: flex;
  align-items: ${props => props.hasImages ? 'flex-start' : 'center'};
  justify-content: center;
  min-height: 96px;
  cursor: pointer;
  box-sizing: border-box;
`

const ImageUploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const ThumbnailsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  position: relative;
`


const GrayCircle = styled.div`
  position: absolute;
  top: -6px;
  left: -6px;
  width: 23.5px;
  height: 23.5px;
  border-radius: 100%;
  background-color: #4F4F4F;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 9999;
`

const DropdownList = styled.div`
  position: absolute;
  background-color: #FFFFFF;
  border-radius: 8px;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin-top: 4px;
`

const DropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  text-align: right;
  &:hover {
    background-color: #F6F6F6;
  }
`

const CtaTypeButton = styled.button<{ active: boolean }>`
    flex: 1;
    padding: 10px;
    border-radius: 8px;
    border: 1.5px solid ${({ active }) => active ? '#007AFF' : '#D1D1D1'};
    background: ${({ active }) => active ? '#E8F2FF' : '#FFFFFF'};
    color: ${({ active }) => active ? '#007AFF' : '#888888'};
    font-size: 14px;
    font-weight: ${({ active }) => active ? 700 : 400};
    cursor: pointer;
    font-family: inherit;
`

const DESCRIPTION_MAX_LENGTH = 1000

const Step1: FC<Step1Props> = ({
    title, setTitle,
    description, setDescription,
    selectedCategory, setSelectedCategory,
    price, setPrice,
    discountPercentage, setDiscountPercentage,
    discounts,
    ctaType, setCtaType,
    ctaValue, setCtaValue,
    couponCode, setCouponCode,
    images, setImages,
    categories
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [showCategoryDropdown, setShowCategoryDropdown] = React.useState(false)
    const [showDiscountDropdown, setShowDiscountDropdown] = React.useState(false)
    // The form state stores the percent as a string (e.g. "20"); look up the matching label
    // for the dropdown's display so the user sees the human-friendly text.
    const selectedDiscount = discounts.find(d => String(d.percent) === discountPercentage)

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const newImages = Array.from(event.target.files).map(file => URL.createObjectURL(file))
            if (images.length + newImages.length <= 4) {
                setImages([...images, ...newImages])
            }
        }
    }

    const handleRemoveImage = (indexToRemove: number) => {
        setImages(images.filter((_, index) => index !== indexToRemove))
    }

    const triggerImageUpload = () => {
        if (images.length < 4) {
            fileInputRef.current?.click()
        }
    }

    return (
        <>
            <FormField>
                <Label>
                    <Text align="right" size="sm" weight="normal" style={{ color: '#000' }}>
                        כותרת ההטבה
                    </Text>
                </Label>
                <Input
                    placeholder="כתבו כותרת מתאימה"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
            </FormField>

            <FormField>
                <Label>
                    <Text size="sm" weight="normal" style={{ color: '#000' }}>
                        תיאור
                    </Text>
                </Label>
                <TextArea
                    placeholder="כתבו תיאור מתאים"
                    value={description}
                    onChange={(e) => {
                        if (e.target.value.length <= DESCRIPTION_MAX_LENGTH) {
                            setDescription(e.target.value)
                        }
                    }}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    rows={6}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                    <Text size="sm" weight="normal" style={{ color: '#888888' }}>
                        ניתן להזין עד {DESCRIPTION_MAX_LENGTH} תווים
                    </Text>
                    <Text size="sm" weight="normal" style={{ color: '#888888' }}>
                        {description.length} / {DESCRIPTION_MAX_LENGTH}
                    </Text>
                </div>
            </FormField>

            <FormField style={{ position: 'relative' }}>
                <Label>
                    <Text size="sm" weight="normal" style={{ color: '#000' }}>
                        קטגוריה
                    </Text>
                </Label>
                <CategoryDropdownContainer
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                >
                    <div style={{ transform: showCategoryDropdown ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}>
                        <BlackPointUpArrowIcon />
                    </div>
                    <Text size="sm" weight="normal" style={{ color: selectedCategory ? "#272727" : "#B0B0B0" }}>
                        {selectedCategory || 'בחר קטגוריה'}
                    </Text>
                </CategoryDropdownContainer>
                {showCategoryDropdown && (
                    <DropdownList>
                        {categories.map((category, index) => (
                            <DropdownItem key={index} onClick={() => {
                                setSelectedCategory(category)
                                setShowCategoryDropdown(false)
                            }}>
                                <Text size="sm" weight="normal" style={{ color: '#272727' }}>{category}</Text>
                            </DropdownItem>
                        ))}
                    </DropdownList>
                )}
            </FormField>

            <FormField>
                <Label>
                    <Text size="sm" weight="normal" style={{ color: '#000' }}>
                        מחיר המוצר
                    </Text>
                </Label>
                <Input
                    placeholder="מחיר המוצר"
                    value={price}
                    onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^0-9.]/g, '')
                        const parts = cleaned.split('.')
                        const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned
                        setPrice(normalized)
                    }}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    onKeyDown={(e) => {
                        if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault()
                        }
                    }}
                />
            </FormField>

            <FormField style={{ position: 'relative' }}>
                <Label>
                    <Text size="sm" weight="normal" style={{ color: '#000' }}>
                        אחוז ההנחה
                    </Text>
                </Label>
                <CategoryDropdownContainer
                    onClick={() => setShowDiscountDropdown(!showDiscountDropdown)}
                >
                    <div style={{ transform: showDiscountDropdown ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}>
                        <BlackPointUpArrowIcon />
                    </div>
                    <Text size="sm" weight="normal" style={{ color: selectedDiscount ? '#272727' : '#B0B0B0' }}>
                        {selectedDiscount ? selectedDiscount.label : 'בחרו הנחה'}
                    </Text>
                </CategoryDropdownContainer>
                {showDiscountDropdown && (
                    <DropdownList>
                        {discounts.map((discount) => (
                            <DropdownItem key={discount.id} onClick={() => {
                                setDiscountPercentage(String(discount.percent))
                                setShowDiscountDropdown(false)
                            }}>
                                <Text size="sm" weight="normal" style={{ color: '#272727' }}>{discount.label}</Text>
                            </DropdownItem>
                        ))}
                    </DropdownList>
                )}
            </FormField>

            <FormField>
                <Label>
                    <Text size="sm" weight="normal" style={{ color: '#000' }}>
                        סוג קישור להמשך
                    </Text>
                </Label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <CtaTypeButton active={ctaType === 'whatsapp'} onClick={() => { setCtaType('whatsapp'); setCouponCode('') }} type="button">
                        WhatsApp
                    </CtaTypeButton>
                    <CtaTypeButton active={ctaType === 'url'} onClick={() => setCtaType('url')} type="button">
                        קישור (URL)
                    </CtaTypeButton>
                </div>
                <Input
                    placeholder={ctaType === 'whatsapp' ? 'מספר טלפון (לדוגמה: 0501234567)' : 'הזינו כתובת URL'}
                    value={ctaValue}
                    onChange={(e) => setCtaValue(e.target.value)}
                />
            </FormField>

            {/* A WhatsApp benefit is redeemed in the chat itself, so there is no code to
                issue or type in — only URL benefits carry a redemption code. */}
            {ctaType === 'url' && (
                <FormField>
                    <Label>
                        <Text size="sm" weight="normal" style={{ color: '#000' }}>
                            קוד קופון למימוש
                        </Text>
                    </Label>
                    <Input
                        placeholder="6 תווים (אותיות או ספרות)"
                        value={couponCode}
                        onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^a-zA-Z0-9א-ת]/g, '');
                            if (cleaned.length <= 6) {
                                setCouponCode(cleaned.toUpperCase());
                            }
                        }}
                        maxLength={6}
                    />
                </FormField>
            )}

            <ImageUploadSection>
                <Label>
                    <Text size="sm" weight="normal" style={{ color: '#000' }}>
                        תמונות (לפחות אחת)
                    </Text>
                </Label>

                <input
                    type="file"
                    hidden
                    ref={fileInputRef}
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                />

                <ImageUploadBox
                    onClick={triggerImageUpload}
                    hasImages={images.length > 0}
                >
                    {images.length > 0 ? (
                        <ThumbnailsContainer>
                            {images.slice(0, 4).map((img, index) => (
                                <SmallImageContainer width={'135px'} border={'1.836px solid var(--Benefits-Borders-Default, #16AA40)'} key={index}>
                                    <SmallImage src={img} alt={`uploaded-${index}`} />
                                    <GrayCircle onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveImage(index);
                                    }}>
                                        <MinusIcon />
                                    </GrayCircle>
                                </SmallImageContainer>
                            ))}
                        </ThumbnailsContainer>
                    ) : (
                        <ImageUploadContent>
                            <ImageUploadIcon />
                            <Text size="sm" weight="normal" style={{ color: '#888888' }}>
                                ניתן להעלות עד 4 תמונות
                            </Text>
                        </ImageUploadContent>
                    )}
                </ImageUploadBox>
            </ImageUploadSection>
        </>
    )
}

export default Step1
